import React, { useMemo } from "react"

import type {
  TranscriptFilterConfig,
  TranscriptRole,
} from "@/types/metrics/metadata"

import { applyFilters, type PreviewMessage } from "../utils/applyFilters"

/** Demo transcript with 20 messages simulating a realistic conversation */
const DEMO_TRANSCRIPT: PreviewMessage[] = [
  { role: "persona", index: 0 },
  { role: "agent", index: 1 },
  { role: "persona", index: 2 },
  { role: "agent", index: 3 },
  { role: "tool", index: 4 },
  { role: "agent", index: 5 },
  { role: "persona", index: 6 },
  { role: "agent", index: 7 },
  { role: "persona", index: 8 },
  { role: "agent", index: 9 },
  { role: "tool", index: 10 },
  { role: "agent", index: 11 },
  { role: "persona", index: 12 },
  { role: "agent", index: 13 },
  { role: "persona", index: 14 },
  { role: "agent", index: 15 },
  { role: "tool", index: 16 },
  { role: "agent", index: 17 },
  { role: "persona", index: 18 },
  { role: "agent", index: 19 },
]

const ROLE_STYLES: Record<TranscriptRole, { bg: string; text: string }> = {
  persona: {
    bg: "bg-blue-100 dark:bg-blue-900",
    text: "text-blue-700 dark:text-blue-200",
  },
  agent: {
    bg: "bg-green-100 dark:bg-green-900",
    text: "text-green-700 dark:text-green-200",
  },
  tool: {
    bg: "bg-purple-100 dark:bg-purple-900",
    text: "text-purple-700 dark:text-purple-200",
  },
}

const ROLE_LABELS: Record<TranscriptRole, string> = {
  persona: "P",
  agent: "A",
  tool: "T",
}

interface TranscriptPreviewProps {
  config: TranscriptFilterConfig
}

export const TranscriptPreview: React.FC<TranscriptPreviewProps> = ({
  config,
}) => {
  const includedIndices = useMemo(
    () => applyFilters(DEMO_TRANSCRIPT, config),
    [config],
  )

  const hasFilters =
    (config.include && config.include.length > 0) ||
    (config.exclude && config.exclude.length > 0)

  if (!hasFilters) {
    return null
  }

  const includedCount = includedIndices.size
  const totalCount = DEMO_TRANSCRIPT.length

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-xs text-coval-text-muted">
          Preview: {includedCount}/{totalCount} messages included
        </span>
        <div className="flex items-center gap-3 text-xs text-coval-text-muted">
          <span className="flex items-center gap-1">
            <span
              className={`w-4 h-4 rounded flex items-center justify-center text-[10px] font-medium ${ROLE_STYLES.persona.bg} ${ROLE_STYLES.persona.text}`}
            >
              P
            </span>
            Persona
          </span>
          <span className="flex items-center gap-1">
            <span
              className={`w-4 h-4 rounded flex items-center justify-center text-[10px] font-medium ${ROLE_STYLES.agent.bg} ${ROLE_STYLES.agent.text}`}
            >
              A
            </span>
            Agent
          </span>
          <span className="flex items-center gap-1">
            <span
              className={`w-4 h-4 rounded flex items-center justify-center text-[10px] font-medium ${ROLE_STYLES.tool.bg} ${ROLE_STYLES.tool.text}`}
            >
              T
            </span>
            Tool
          </span>
        </div>
      </div>
      <div className="flex gap-1 w-full">
        {DEMO_TRANSCRIPT.map((msg) => {
          const isIncluded = includedIndices.has(msg.index)
          const styles = ROLE_STYLES[msg.role]
          return (
            <div
              key={msg.index}
              className={`
                flex-1 h-6 rounded flex items-center justify-center text-xs font-medium
                transition-opacity duration-200
                ${styles.bg} ${styles.text}
                ${isIncluded ? "opacity-100" : "opacity-20"}
              `}
              title={`${msg.role} - ${isIncluded ? "included" : "excluded"}`}
            >
              {ROLE_LABELS[msg.role]}
            </div>
          )
        })}
      </div>
    </div>
  )
}
